/**
 * Reusable display components extracted from DogDetailsCard
 */
import React from "react";
import { Box, Typography, Stack, Chip, Grid } from "@mui/material";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import ScaleRoundedIcon from "@mui/icons-material/ScaleRounded";
import type { Dog } from "../../types";
import { fmtAge, fmtList } from "../../utils/format";
import { caloriesFromGrams } from "@/domains/nutrition/FoodEnergy";
import { QUIZ } from "@/theme/quizTokens";

// ── Personality Badges ──

type PersonalityBadge = {
  label: string;
  color: string;
  bgColor: string;
  icon?: string;
};

export function getPersonalityBadges(dog: Dog): PersonalityBadge[] {
  const badges: PersonalityBadge[] = [];
  const age = dog.ageYears ?? 0;

  if (age < 1) {
    badges.push({ label: "Puppy", color: "#9C27B0", bgColor: "#F3E5F5", icon: "🐕" });
  } else if (age >= 7) {
    badges.push({ label: "Senior Pup", color: "#795548", bgColor: "#EFEBE9", icon: "👴" });
  } else {
    badges.push({ label: "Adult", color: "#1A57D3", bgColor: "#E6F0FF", icon: "🐶" });
  }

  const activity = String(dog.activityLevel || "").toUpperCase();
  if (activity === "HIGH" || activity.includes("90") || activity.includes("60")) {
    badges.push({ label: "Active Pup", color: "#2E7D32", bgColor: "#E5FFEA", icon: "🏃" });
  } else if (activity === "LOW" || activity.includes("0–30") || activity.includes("0-30")) {
    badges.push({ label: "Relaxed", color: "#CE7A00", bgColor: "#FFF0D6", icon: "😴" });
  }

  const condition = String(dog.bodyCondition || "").toLowerCase();
  if (condition === "ideal") {
    badges.push({ label: "Healthy Weight", color: "#2E7D32", bgColor: "#E5FFEA", icon: "💪" });
  }

  if (dog.allergies && dog.allergies.length > 0) {
    badges.push({ label: "Allergy Alert", color: "#CE7A00", bgColor: "#FFF0D6", icon: "⚠️" });
  }
  if (dog.healthIssues && dog.healthIssues.length > 0) {
    badges.push({ label: "Special Care", color: "#D14C3F", bgColor: "#FFE5E2", icon: "❤️" });
  }

  return badges;
}

// ── PersonalityBadgesRow ──

export function PersonalityBadgesRow({ dog }: { dog: Dog }) {
  const badges = getPersonalityBadges(dog);
  if (badges.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
      {badges.map((badge, idx) => (
        <Chip
          key={idx}
          label={`${badge.icon || ""} ${badge.label}`}
          size="small"
          sx={{
            height: 28,
            fontSize: 12,
            fontWeight: 700,
            backgroundColor: badge.bgColor,
            color: badge.color,
            "& .MuiChip-label": { px: 1.5 },
          }}
        />
      ))}
    </Box>
  );
}

// ── Activity Gauge ──

export function ActivityGauge({ level }: { level: string }) {
  const levelMap: Record<string, number> = {
    "LOW": 20,
    "0–30 minutes/day": 20,
    "0-30 minutes/day": 20,
    "MEDIUM": 50,
    "30–60 minutes/day": 50,
    "30-60 minutes/day": 50,
    "45–60 minutes/day": 60,
    "45-60 minutes/day": 60,
    "0–45 minutes/day": 35,
    "0-45 minutes/day": 35,
    "HIGH": 85,
    "60–90 minutes/day": 85,
    "60-90 minutes/day": 85,
    "90+ minutes/day": 100,
  };

  const percentage = levelMap[level] ?? 50;
  const getColor = (pct: number) => {
    if (pct <= 30) return { bar: "#FFB74D", bg: "#FFF3E0" };
    if (pct <= 60) return { bar: "#4CAF50", bg: "#E8F5E9" };
    return { bar: "#2196F3", bg: "#E3F2FD" };
  };
  const colors = getColor(percentage);

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: QUIZ.color.brown, opacity: 0.65 }}>
          Activity Level
        </Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: colors.bar }}>
          {level || "Unknown"}
        </Typography>
      </Box>
      <Box sx={{ height: 10, borderRadius: "5px", backgroundColor: colors.bg, overflow: "hidden" }}>
        <Box
          sx={{
            height: "100%",
            width: `${percentage}%`,
            borderRadius: "5px",
            backgroundColor: colors.bar,
            transition: "width 0.5s ease",
          }}
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
        <Typography sx={{ fontSize: 10, color: QUIZ.color.brown, opacity: 0.5 }}>Low</Typography>
        <Typography sx={{ fontSize: 10, color: QUIZ.color.brown, opacity: 0.5 }}>High</Typography>
      </Box>
    </Box>
  );
}

// ── Body Condition Scale ──

export function BodyConditionScale({ condition }: { condition: string }) {
  const conditionLower = String(condition || "").toLowerCase();
  const positions = ["underweight", "ideal", "overweight"];
  const activeIndex = positions.indexOf(conditionLower);

  const getColor = (idx: number, isActive: boolean) => {
    if (!isActive) return { bg: "#F0F0F0", text: "#999" };
    if (idx === 0) return { bg: "#FFF0D6", text: "#CE7A00" };
    if (idx === 1) return { bg: "#E5FFEA", text: "#2E7D32" };
    return { bg: "#FFE5E2", text: "#D14C3F" };
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: QUIZ.color.brown, opacity: 0.65, mb: 1 }}>
        Body Condition
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5 }}>
        {positions.map((pos, idx) => {
          const isActive = idx === activeIndex;
          const colors = getColor(idx, isActive);
          return (
            <Box
              key={pos}
              sx={{
                flex: 1,
                py: 1,
                px: 1,
                borderRadius: "8px",
                backgroundColor: colors.bg,
                textAlign: "center",
                border: isActive ? `2px solid ${colors.text}` : "2px solid transparent",
                transition: "all 0.2s ease",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 10, sm: 11 },
                  fontWeight: isActive ? 800 : 600,
                  color: colors.text,
                  textTransform: "capitalize",
                }}
              >
                {pos}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Age Display ──

function dogToHumanYears(years: number, months: number = 0): number {
  const totalYears = years + months / 12;
  if (totalYears <= 0) return 0;
  return Math.round(totalYears * 7);
}

export function AgeDisplay({ dog }: { dog: Dog }) {
  const years = dog.ageYears ?? 0;
  const months = dog.ageMonths ?? 0;
  const humanYears = dogToHumanYears(years, months);

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "14px",
        background: "linear-gradient(135deg, #E6F0FF 0%, #F3E5F5 100%)",
        display: "flex",
        alignItems: "center",
        gap: 2,
        mb: 2,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "12px",
          backgroundColor: QUIZ.color.warmWhite,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 24,
        }}
      >
        🎂
      </Box>
      <Box>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: QUIZ.color.brown }}>
          {fmtAge(dog)}
        </Typography>
        <Typography sx={{ fontSize: 13, color: QUIZ.color.brown, opacity: 0.7 }}>
          ≈ <strong>{humanYears}</strong> in human years
        </Typography>
      </Box>
    </Box>
  );
}

// ── Metric Card ──

export function MetricCard({
  icon,
  value,
  label,
  color = "primary",
}: {
  icon: React.ReactNode;
  value: React.ReactNode;
  label: string;
  color?: "primary" | "success" | "info";
}) {
  const colorMap = {
    primary: { bg: QUIZ.color.cream2, iconBg: "#FFD1CC", text: QUIZ.color.brown },
    success: { bg: "#E5FFEA", iconBg: "#D1FFE0", text: "#2E7D32" },
    info: { bg: "#E6F0FF", iconBg: "#D1E5FF", text: "#1A57D3" },
  };
  const colors = colorMap[color];

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "14px",
        backgroundColor: colors.bg,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          backgroundColor: colors.iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: colors.text,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: { xs: 20, md: 22 }, lineHeight: 1.1, color: QUIZ.color.brown }}>
          {value}
        </Typography>
        <Typography sx={{ fontSize: 12, opacity: 0.65, fontWeight: 600, color: QUIZ.color.brown }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

// ── Plan Snapshot Section ──

export function PlanSnapshotSection({ dog }: { dog: Dog }) {
  const dailyGrams = dog.dailyGrams ?? dog.currentNutrition?.dailyGrams ?? null;
  // Derive from grams so legacy stored calories (pre-rounding MER) aren't shown
  const dailyCalories =
    dailyGrams != null
      ? caloriesFromGrams(dailyGrams)
      : dog.dailyCalories ?? dog.currentNutrition?.dailyCalories ?? null;

  if (dailyCalories == null && dailyGrams == null) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{ fontSize: 14, fontWeight: 800, color: QUIZ.color.brown, mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}
      >
        Plan Snapshot
      </Typography>
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 6 }}>
          <MetricCard
            icon={<LocalFireDepartmentRoundedIcon sx={{ fontSize: 22 }} />}
            value={dailyCalories != null ? Math.round(Number(dailyCalories)) : "—"}
            label="daily calories"
            color="primary"
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <MetricCard
            icon={<ScaleRoundedIcon sx={{ fontSize: 22 }} />}
            value={dailyGrams != null ? `${Math.round(Number(dailyGrams))}g` : "—"}
            label="daily serving"
            color="success"
          />
        </Grid>
      </Grid>
    </Box>
  );
}

// ── Basics Section ──

export function BasicsSection({ dog }: { dog: Dog }) {
  const sexLabel = dog.sex === "MALE" ? "Male" : dog.sex === "FEMALE" ? "Female" : dog.sex || "—";
  const reproLabel = dog.reproduction || "—";

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{ fontSize: 14, fontWeight: 800, color: QUIZ.color.brown, mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}
      >
        Basics
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
        <DetailItem label="Name" value={dog.name || "—"} />
        <DetailItem label="Breed" value={dog.breed || "—"} />
        <DetailItem label="Sex" value={sexLabel} />
        <DetailItem label="Reproduction" value={reproLabel} />
      </Box>
      <Box sx={{ mt: 1.5 }}>
        <AgeDisplay dog={dog} />
      </Box>
    </Box>
  );
}

// ── Physical Section ──

export function PhysicalSection({ dog }: { dog: Dog }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{ fontSize: 14, fontWeight: 800, color: QUIZ.color.brown, mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}
      >
        Physical
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 1.5,
          borderRadius: "10px",
          backgroundColor: "#F4F0E8",
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            backgroundColor: "#E5FFEA",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ScaleRoundedIcon sx={{ fontSize: 20, color: "#2E7D32" }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, color: QUIZ.color.brown, opacity: 0.65 }}>Weight</Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: QUIZ.color.brown }}>
            {dog.weightKg != null ? `${dog.weightKg} kg` : "—"}
          </Typography>
        </Box>
      </Box>
      <ActivityGauge level={dog.activityLevel || ""} />
      <BodyConditionScale condition={dog.bodyCondition || ""} />
    </Box>
  );
}

// ── Health Section ──

export function HealthSection({ dog }: { dog: Dog }) {
  const hasAllergies = dog.allergies && dog.allergies.length > 0;
  const hasHealth = dog.healthIssues && dog.healthIssues.length > 0;
  if (!hasAllergies && !hasHealth) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{ fontSize: 14, fontWeight: 800, color: QUIZ.color.brown, mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}
      >
        Health Notes
      </Typography>
      <Stack spacing={1}>
        {hasAllergies && (
          <Box sx={{ p: 1.5, borderRadius: "10px", backgroundColor: "#FFF0D6", border: "1px solid #FFE0A0" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#CE7A00", mb: 0.5, textTransform: "uppercase" }}>
              Allergies
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: QUIZ.color.brown }}>
              {fmtList(dog.allergies)}
            </Typography>
          </Box>
        )}
        {hasHealth && (
          <Box sx={{ p: 1.5, borderRadius: "10px", backgroundColor: "#FFE5E2", border: "1px solid #FFCCC7" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#D14C3F", mb: 0.5, textTransform: "uppercase" }}>
              Health Considerations
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: QUIZ.color.brown }}>
              {fmtList(dog.healthIssues)}
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

// ── Detail Item helper ──

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: QUIZ.color.brown, opacity: 0.55, textTransform: "uppercase", mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: QUIZ.color.brown }}>
        {value}
      </Typography>
    </Box>
  );
}
